"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Upload,
  X,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Folder,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "@workos-inc/authkit-react";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import api from "../http";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { useToast } from "@/hooks/use-toast";
import { useAssistantAvatar } from "@/contexts/AssistantAvatarContext";

interface Document {
  id: string;
  name: string;
  type: string;
  selected: boolean;
  created_at: string;
  email: string;
  profile_image_url?: string | null;
  status?: string;
  sharepoint_full_path?: string | null;
  sharepoint_id?: string | null;
}

interface TreeFile {
  id: string;
  name: string;
  status?: string;
}

interface TreeNode {
  name: string;
  path: string;
  folders: TreeNode[];
  files: TreeFile[];
  file_count?: number;
}

export default function Documents() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const { getAccessToken, user } = useAuth();

  // Modal + upload state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileList | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [nextPage, setNextPage] = useState(1);
  const [previousPage, setPreviousPage] = useState(1);
  const [documentFilter, setDocumentFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Folder tree state
  const [folderTree, setFolderTree] = useState<TreeNode | null>(null);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<string>("/");
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({
    "/": true,
  });
  const [isMobileTreeOpen, setIsMobileTreeOpen] = useState(false);

  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  const { toast } = useToast();
  const isSharePointEnabled = appConfig.features.sharePoint;
  const { currentAssistant } = useAssistantAvatar();

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  // Helper to expand all parent paths to a given folder
  const getParentPaths = useCallback((path: string): Record<string, boolean> => {
    const paths: Record<string, boolean> = { "/": true };
    if (!path || path === "/") return paths;
    
    const segments = path.split("/").filter(Boolean);
    let currentPath = "";
    for (const segment of segments) {
      currentPath += "/" + segment;
      paths[currentPath] = true;
    }
    return paths;
  }, []);

  const loadFolderTree = useCallback(async () => {
    try {
      setIsTreeLoading(true);
      const token = await getAccessToken();
      const data = await api.getDocumentTree(token);
      setFolderTree(data);
      setExpandedPaths((prev) => ({
        "/": true,
        ...prev,
        [data.path]: true,
      }));
      // Don't override selectedFolder here - let the assistant effect handle it
    } catch (error) {
      console.error("Failed to load folder tree:", error);
    } finally {
      setIsTreeLoading(false);
    }
  }, [getAccessToken]);

  const findNodeByPath = useCallback(
    (node: TreeNode | null, path: string): TreeNode | null => {
      if (!node) return null;
      if (node.path === path) return node;
      for (const child of node.folders ?? []) {
        const result = findNodeByPath(child, path);
        if (result) {
          return result;
        }
      }
      return null;
    },
    [],
  );

  // Filter the folder tree to only show the assistant's root folder subtree
  const filteredFolderTree = useMemo(() => {
    if (!folderTree) return null;
    
    const rootFolder = currentAssistant?.sharepoint_root_folder;
    if (!rootFolder || rootFolder === "/") {
      // No filter - show full tree
      return folderTree;
    }
    
    // Find the subtree at the assistant's root folder
    const subtree = findNodeByPath(folderTree, rootFolder);
    if (subtree) {
      // Return the subtree as the new root
      return subtree;
    }
    
    // If the folder doesn't exist in the tree, show full tree as fallback
    return folderTree;
  }, [folderTree, currentAssistant?.sharepoint_root_folder, findNodeByPath]);

  const currentFolderNode = useMemo(
    () => findNodeByPath(filteredFolderTree, selectedFolder || "/"),
    [filteredFolderTree, selectedFolder, findNodeByPath],
  );

  // Display name for the selected folder (shows folder name instead of path)
  const selectedFolderDisplayName = useMemo(() => {
    if (currentFolderNode?.name) {
      return currentFolderNode.name;
    }
    // Fallback: extract last segment from path or show "Root"
    if (!selectedFolder || selectedFolder === "/") {
      return "Root";
    }
    const segments = selectedFolder.split("/").filter(Boolean);
    return segments[segments.length - 1] || "Root";
  }, [currentFolderNode, selectedFolder]);

  const handleFolderSelect = useCallback(
    (path: string) => {
      setSelectedFolder(path);
      setCurrentPage(1);
      setIsMobileTreeOpen(false);
      // Clear documents immediately to prevent showing stale data
      setDocuments([]);
    },
    [],
  );

  const fetchDocuments = async () => {
    const token = await getAccessToken();
    setIsDocumentsLoading(true);
    try {
      const folderFilter =
        isSharePointEnabled && selectedFolder && selectedFolder !== "/" ? selectedFolder : null;
      const data = await api.getDocuments(
        token,
        currentPage,
        documentFilter,
        sortField,
        sortDirection,
        folderFilter,
      );
      if (data) {
        setDocuments(data.documents);
        setTotalPages(data.totalPages);
        setTotalDocuments(data.totalDocuments || 0);
        setNextPage(data.nextPage);
        setPreviousPage(data.previousPage);
      }
    } catch (error) {
      console.error("Error fetching Documents:", error);
    } finally {
      setIsDocumentsLoading(false);
    }
  };

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleRefresh = async () => {
    if (isSharePointEnabled) {
      await loadFolderTree();
    }
    await fetchDocuments();
  };

  // Track if initial folder has been set (to avoid fetching with wrong folder on mount)
  const [isInitialFolderSet, setIsInitialFolderSet] = useState(!isSharePointEnabled);

  useEffect(() => {
    // Only fetch if the initial folder has been set (or SharePoint is disabled)
    if (isInitialFolderSet) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, documentFilter, sortField, sortDirection, selectedFolder, isInitialFolderSet]);

  useEffect(() => {
    if (isSharePointEnabled) {
      loadFolderTree();
    } else {
      // If SharePoint is disabled, fetch documents immediately
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharePointEnabled]);

  // Update selected folder when assistant changes
  useEffect(() => {
    if (isSharePointEnabled && currentAssistant?.sharepoint_root_folder) {
      const rootFolder = currentAssistant.sharepoint_root_folder;
      setSelectedFolder(rootFolder);
      setExpandedPaths((prev) => ({
        ...prev,
        ...getParentPaths(rootFolder),
        [rootFolder]: true,
      }));
      setCurrentPage(1);
      // Clear documents and mark initial folder as set
      setDocuments([]);
      setIsInitialFolderSet(true);
    } else if (isSharePointEnabled && currentAssistant && !currentAssistant.sharepoint_root_folder) {
      // Assistant loaded but has no sharepoint_root_folder - use root
      setIsInitialFolderSet(true);
    }
  }, [isSharePointEnabled, currentAssistant, getParentPaths]);

  // Poll for status updates if there are processing documents
  useEffect(() => {
    const hasProcessingDocs = documents.some(
      (doc) => doc.status === "pending" || doc.status === "processing",
    );

    if (hasProcessingDocs) {
      const interval = setInterval(() => {
        fetchDocuments();
      }, 2000); // Poll every 2 seconds for faster updates

      return () => clearInterval(interval);
    }
  }, [documents]);

  // Handle the actual file(s) uploading
  const handleFiles = async (files: File[]) => {
    if (isUploading || files.length === 0) return;

    setIsUploading(true);

    try {
      const token = await getAccessToken();
      const response = await api.upload(
        files,
        token,
        isSharePointEnabled ? (selectedFolder || "/") : "/",
      );

      if (response.failed.length > 0) {
        const failedNames = response.failed
          .map((f) => `${f.filename} (${f.reason})`)
          .join(", ");
        toast({
          title: "Upload teilweise fehlgeschlagen",
          description: `${response.failed.length} Datei(en) konnten nicht hochgeladen werden: ${failedNames}`,
          variant: "destructive",
        });
      }

      if (response.uploaded.length > 0) {
        toast({
          title: "Upload erfolgreich",
          description: isSharePointEnabled
            ? `${response.uploaded.length} Datei(en) wurden nach "${selectedFolderDisplayName}" übertragen. Sie erscheinen nach dem nächsten Sync.`
            : `${response.uploaded.length} Datei(en) wurden hochgeladen. Verarbeitung läuft.`,
        });
      }

      clearAllFiles();
      setIsUploadModalOpen(false);
      await loadFolderTree();
      await fetchDocuments();
    } catch (error) {
      console.error("Error uploading files:", error);
      toast({
        title: "Upload fehlgeschlagen",
        description:
          error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Called when user clicks "Upload" button in modal
  const handleUpload = () => {
    if (selectedFile) {
      handleFiles(uploadFiles);
    }
  };

  // IFC file size constants
  const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "xlsx", "csv", "ifc"];
  const MAX_FILE_SIZE_DEFAULT = 25 * 1024 * 1024; // 25MB
  const MAX_FILE_SIZE_IFC = 500 * 1024 * 1024; // 500MB for IFC
  const MAX_TOTAL_SIZE_DEFAULT = 100 * 1024 * 1024; // 100MB
  const MAX_TOTAL_SIZE_IFC = 500 * 1024 * 1024; // 500MB when IFC present

  const getExtension = (file: File) =>
    file.name.split(".").pop()?.toLowerCase() || "";

  const getMaxSizeForFile = (file: File) =>
    getExtension(file) === "ifc" ? MAX_FILE_SIZE_IFC : MAX_FILE_SIZE_DEFAULT;

  const getTotalSizeLimit = (files: File[]) =>
    files.some((file) => getExtension(file) === "ifc")
      ? MAX_TOTAL_SIZE_IFC
      : MAX_TOTAL_SIZE_DEFAULT;

  const validateFiles = (files: File[]) => {
    const validFiles = files.filter((file) =>
      ALLOWED_EXTENSIONS.includes(getExtension(file)),
    );

    if (validFiles.length !== files.length) {
      toast({
        title: "Ungültiger Dateityp",
        description: `Nur ${ALLOWED_EXTENSIONS.join(", ").toUpperCase()} Dateien sind erlaubt.`,
        variant: "destructive",
      });
    }

    if (validFiles.length === 0) {
      return null;
    }

    const oversizedFiles = validFiles.filter(
      (file) => file.size > getMaxSizeForFile(file),
    );
    if (oversizedFiles.length > 0) {
      toast({
        title: "Datei zu groß",
        description: `Folgende Dateien sind zu groß (max 25MB, IFC bis 500MB): ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}`,
        variant: "destructive",
      });
      return null;
    }

    const totalSizeLimit = getTotalSizeLimit(validFiles);
    const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > totalSizeLimit) {
      toast({
        title: "Gesamtgröße überschritten",
        description: `Gesamtgröße überschreitet ${Math.round(totalSizeLimit / (1024 * 1024))}MB Limit.`,
        variant: "destructive",
      });
      return null;
    }

    return validFiles;
  };

  // Called when user selects a file
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const validFiles = validateFiles(files);
    if (!validFiles) return;

    setSelectedFile(e.target.files);
    setUploadFiles(validFiles);
  };

  // Add function to remove individual files
  const removeFile = (indexToRemove: number) => {
    const newFiles = uploadFiles.filter((_, index) => index !== indexToRemove);
    setUploadFiles(newFiles);

    // Update FileList for consistency
    if (newFiles.length === 0) {
      setSelectedFile(null);
    } else {
      const dt = new DataTransfer();
      newFiles.forEach((file) => dt.items.add(file));
      setSelectedFile(dt.files);
    }
  };

  // Add function to clear all files
  const clearAllFiles = () => {
    setUploadFiles([]);
    setSelectedFile(null);
  };

  // Add drag & drop handlers
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files);

    const validDroppedFiles = validateFiles(droppedFiles);
    if (!validDroppedFiles) return;

    // Add to existing files
    const newFiles = [...uploadFiles, ...validDroppedFiles];

    // Validate total size with combined files
    const totalSizeLimit = getTotalSizeLimit(newFiles);
    const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > totalSizeLimit) {
      toast({
        title: "Gesamtgröße überschritten",
        description: `Gesamtgröße überschreitet ${Math.round(totalSizeLimit / (1024 * 1024))}MB Limit.`,
        variant: "destructive",
      });
      return;
    }

    setUploadFiles(newFiles);

    // Update FileList
    const dt = new DataTransfer();
    newFiles.forEach((file) => dt.items.add(file));
    setSelectedFile(dt.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getTotalFiles = (node: TreeNode | null): number => {
    if (!node) {
      return 0;
    }
    const childCount = node.folders.reduce(
      (sum, child) => sum + getTotalFiles(child),
      0,
    );
    return (node.files?.length ?? 0) + childCount;
  };

  const toggleFolder = (path: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [path]: !(prev[path] ?? (path === "/")),
    }));
  };

  const renderFolderNode = (node: TreeNode, depth = 0): JSX.Element => {
    const isRoot = node.path === "/";
    const isExpanded = expandedPaths[node.path] ?? isRoot;
    const isSelected = selectedFolder === node.path;
    const totalFiles = getTotalFiles(node);
    const hasChildren = node.folders.length > 0;

    return (
      <div key={node.path}>
        <div
          className={`flex items-center justify-between rounded-md py-1 px-2 text-sm cursor-pointer transition-colors ${
            isSelected
              ? ""
              : "text-gray-700 hover:bg-gray-100"
          }`}
          style={{
            paddingLeft: depth * 12,
            ...(isSelected ? { backgroundColor: `${APP_COLOR}15`, color: APP_COLOR } : {})
          }}
          onClick={() => handleFolderSelect(node.path)}
        >
          <div className="flex items-center gap-2 min-w-0">
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(node.path);
                }}
                className="p-0.5 rounded hover:bg-gray-200"
              >
                {isExpanded ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <Folder size={14} />
            <span className="truncate">
              {node.name || (isRoot ? "Root" : "(Unbenannt)")}
            </span>
          </div>
          <span className="text-xs text-gray-500">{totalFiles}</span>
        </div>
        {isExpanded &&
          node.folders.map((child) => renderFolderNode(child, depth + 1))}
      </div>
    );
  };

  // Deletion handler
  const handleDelete = async (documentId: string) => {
    try {
      const token = await getAccessToken();
      const result = await api.deleteDocuments(documentId, token);
      if (result) {
        setDocuments((prevDocs) =>
          prevDocs.filter((doc) => doc.id !== documentId),
        );
        toast({
          title: "Löschen erfolgreich",
          description: isSharePointEnabled
            ? "Das Dokument wird aus SharePoint entfernt. Es verschwindet hier nach dem nächsten Sync."
            : "Das Dokument wurde gelöscht.",
        });
      }
    } catch (error) {
      console.error("Error deleting Document:", error);
      toast({
        title: "Löschen fehlgeschlagen",
        description: "Das Dokument konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  // Document click handler - view/download document
  const handleDocumentClick = async (documentId: string, documentName: string) => {
    try {
      const token = await getAccessToken();
      
      // Check if it's a PDF based on file extension
      const isPdf = documentName.toLowerCase().endsWith('.pdf');
      
      if (isPdf) {
        // For PDFs: Open URL directly in new tab so headers are preserved
        const downloadUrl = `${import.meta.env.VITE_BACKEND_URL}/documents/download/${documentId}`;
        
        // Create a temporary form to POST the request with auth token
        // This is more reliable than window.open for authenticated requests
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // For authenticated requests, we need to use fetch and blob
        const response = await fetch(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to open PDF: ${documentName}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Open in new tab - browser will display PDF inline
        const newWindow = window.open(blobUrl, '_blank');
        
        // Clean up blob URL after window opens
        if (newWindow) {
          newWindow.onload = () => {
            // Give the browser time to load the PDF before revoking the URL
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
          };
        } else {
          // If popup was blocked, clean up immediately
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        }
      } else {
        // For other files: Download with original filename
        const downloadUrl = `${import.meta.env.VITE_BACKEND_URL}/documents/download/${documentId}`;
        
        const response = await fetch(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to download document: ${documentName}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Trigger download with original filename
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
      
    } catch (error) {
      console.error(`Error downloading document "${documentName}":`, error);
      toast({
        title: "Öffnen fehlgeschlagen",
        description: `Dokument "${documentName}" konnte nicht geöffnet werden.`,
        variant: "destructive",
      });
    }
  };

  // Get status badge component
  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case "pending":
        return (
          <div className="flex items-center gap-1 text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full text-xs">
            <Clock size={12} />
            Ausstehend
          </div>
        );
      case "processing":
        return (
          <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
            <Loader2 size={12} className="animate-spin" />
            Wird verarbeitet
          </div>
        );
      case "failed":
        return (
          <div className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs">
            <AlertCircle size={12} />
            Fehlgeschlagen
          </div>
        );
      case "processed":
      default:
        return (
          <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
            <CheckCircle size={12} />
            Verarbeitet
          </div>
        );
    }
  };

  // Count documents by status
  const processingCount = documents.filter(
    (doc) => doc.status === "pending" || doc.status === "processing",
  ).length;

  // Update the modal close handler
  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    clearAllFiles();
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      {/* Folder Sidebar - only show when SharePoint is enabled */}
      {isSharePointEnabled && (
      <div className="hidden lg:flex w-72 flex-col border-r border-gray-200 bg-white">
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">SharePoint Ordner</p>
              {isSharePointEnabled && (
              <p className="text-xs text-gray-500 truncate">
                Ziel: {selectedFolderDisplayName}
              </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 h-8 px-3 py-1"
            >
              Aktualisieren
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3">
          {isTreeLoading ? (
            <div className="flex items-center justify-center text-gray-500 text-sm">
              <Loader2 size={16} className="mr-2 animate-spin" />
              Ordner werden geladen...
            </div>
          ) : filteredFolderTree ? (
            renderFolderNode(filteredFolderTree)
          ) : (
            <p className="text-sm text-gray-500">Keine Ordner gefunden</p>
          )}
        </div>
      </div>
      )}
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold">Dokumente</h1>
          <div className="flex items-center gap-2">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto pb-20 lg:pb-8">
          {/* Mobile Header - only show on mobile */}
          <div className="lg:hidden mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  Hochgeladene Dokumente
                </span>
                <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                  <span
                    className={`font-bold text-xs`}
                    style={{ color: APP_COLOR }}
                  >
                    {totalDocuments}{" "}
                    {totalDocuments === 1 ? "Dokument" : "Dokumente"}
                  </span>
                </div>
                {processingCount > 0 && (
                  <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
                    <Loader2 size={10} className="animate-spin" />
                    {processingCount} werden verarbeitet
                  </div>
                )}
              </div>
              <button
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-8 px-3 py-1"
                style={{ backgroundColor: APP_COLOR }}
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Upload size={14} className="mr-1" />
                <span className="text-xs">Hochladen</span>
              </button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-lg font-medium">
                Hochgeladene Dokumente
              </span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className={`font-bold text-xs`}
                  style={{ color: APP_COLOR }}
                >
                  {totalDocuments}{" "}
                  {totalDocuments === 1 ? "Dokument" : "Dokumente"}
                </span>
              </div>
              {processingCount > 0 && (
                <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
                  <Loader2 size={12} className="animate-spin" />
                  {processingCount} werden parallel verarbeitet
                </div>
              )}
              {isSharePointEnabled && (
              <span className="text-xs text-gray-500">
                Zielordner: <span className="font-medium">{selectedFolderDisplayName}</span>
              </span>
              )}
            </div>

            <div className="relative w-[300px]">
              <input
                type="text"
                placeholder="Dokumente suchen..."
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setDocumentFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>

            <div className="flex items-center gap-4">
              <button
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2`}
                style={{ backgroundColor: APP_COLOR }}
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Upload size={16} className="mr-2" />
                <span>Neu hochladen</span>
              </button>
            </div>
          </div>

          {/* Mobile Folder Tree - only show when SharePoint is enabled */}
          {isSharePointEnabled && (
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setIsMobileTreeOpen((prev) => !prev)}
              className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <span>
                Zielordner:{" "}
                <span className="font-medium">{selectedFolderDisplayName}</span>
              </span>
              {isMobileTreeOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isMobileTreeOpen && (
              <div className="mt-3 max-h-64 overflow-auto border border-gray-200 rounded-lg bg-white p-3">
                {isTreeLoading ? (
                  <div className="flex items-center justify-center text-gray-500 text-sm">
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Ordner werden geladen...
                  </div>
                ) : filteredFolderTree ? (
                  renderFolderNode(filteredFolderTree)
                ) : (
                  <p className="text-sm text-gray-500">Keine Ordner gefunden</p>
                )}
              </div>
            )}
          </div>
          )}

          {/* Mobile Search - only show on mobile */}
          <div className="lg:hidden mb-4">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Dokumente suchen..."
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setDocumentFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>
          </div>

          {/* Subfolders grid */}
          {/* Mobile Document Cards */}
          <div className="lg:hidden flex-1">
            {currentFolderNode && currentFolderNode.folders.length > 0 && (
              <div className="space-y-3 mb-4">
                {currentFolderNode.folders.map((folder) => (
                  <button
                    key={`mobile-folder-${folder.path}`}
                    type="button"
                    onClick={() => handleFolderSelect(folder.path)}
                    className="w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between shadow-sm"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="rounded-md p-2" style={{ backgroundColor: `${APP_COLOR}15`, color: APP_COLOR }}>
                        <Folder size={18} />
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="font-medium truncate">
                          {folder.name || "(Unbenannt)"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {folder.file_count ?? 0} Dateien
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-500" />
                  </button>
                ))}
              </div>
            )}
            {isDocumentsLoading ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
                Dokumente werden geladen...
              </div>
            ) : documents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Keine Dokumente gefunden
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((document) => (
                  <div
                    key={document.id}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div 
                          className="flex items-center gap-2 mb-2 cursor-pointer transition-colors group"
                          style={{ ['--hover-color' as string]: APP_COLOR }}
                          onClick={() => handleDocumentClick(document.id, document.name)}
                          onMouseEnter={(e) => e.currentTarget.style.color = APP_COLOR}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                        >
                          <div className="flex-shrink-0 p-1 rounded-md" style={{ backgroundColor: `${APP_COLOR}20` }}>
                            <FileText
                              size={16}
                              style={{ color: APP_COLOR }}
                            />
                          </div>
                          <span className="font-medium text-sm truncate group-hover:underline">
                            {document.name}
                          </span>
                        </div>
                        {isSharePointEnabled && (
                        <div className="text-xs text-gray-500 mb-2">
                          {document.sharepoint_full_path || "Kein SharePoint-Pfad"}
                        </div>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage
                              src={document.profile_image_url || undefined}
                              alt="User avatar"
                            />
                            <AvatarFallback className="text-xs">
                              {document.email.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-gray-500">
                            {document.email}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {new Date(document.created_at).toLocaleString(
                            "de-DE",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            },
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(document.id)}
                        className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                        disabled={
                          document.status === "processing" ||
                          document.status === "pending"
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      {getStatusBadge(document.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table Container */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[3fr_2fr_2fr_1fr_1fr] bg-gray-50 text-gray-700 border-b border-gray-200">
              <div className="p-4 font-medium flex items-center gap-1">
                Dateiname <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'name' && sortDirection === 'asc' ? "" : "text-gray-400"} style={sortField === 'name' && sortDirection === 'asc' ? { color: APP_COLOR } : undefined} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'name' && sortDirection === 'desc' ? "" : "text-gray-400"} style={sortField === 'name' && sortDirection === 'desc' ? { color: APP_COLOR } : undefined} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                Hochgeladen von{" "}
                <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'email' && sortDirection === 'asc' ? "" : "text-gray-400"} style={sortField === 'email' && sortDirection === 'asc' ? { color: APP_COLOR } : undefined} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'email' && sortDirection === 'desc' ? "" : "text-gray-400"} style={sortField === 'email' && sortDirection === 'desc' ? { color: APP_COLOR } : undefined} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                Datum/Zeit <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'date' && sortDirection === 'asc' ? "" : "text-gray-400"} style={sortField === 'date' && sortDirection === 'asc' ? { color: APP_COLOR } : undefined} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'date' && sortDirection === 'desc' ? "" : "text-gray-400"} style={sortField === 'date' && sortDirection === 'desc' ? { color: APP_COLOR } : undefined} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium">Status</div>
              <div className="p-4 font-medium">Aktionen</div>
            </div>

            {/* Table Rows */}
            {currentFolderNode?.folders.map((folder) => (
              <div
                key={`folder-row-${folder.path}`}
                className="grid grid-cols-[3fr_2fr_2fr_1fr_1fr] border-b border-gray-200"
                style={{ backgroundColor: `${APP_COLOR}08` }}
              >
                <div
                  className="p-4 flex items-center gap-2 cursor-pointer transition-colors group"
                  style={{ color: 'inherit' }}
                  onClick={() => handleFolderSelect(folder.path)}
                >
                  <div className="flex-shrink-0 p-1 rounded-md" style={{ backgroundColor: `${APP_COLOR}20`, color: APP_COLOR }}>
                    <Folder className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium group-hover:underline">
                      {folder.name || "(Unbenannt)"}
                    </span>
                    <span className="text-xs text-gray-600 truncate">
                      Ordner • {folder.file_count ?? 0} Dateien
                    </span>
                  </div>
                </div>
                <div className="p-4 flex items-center text-gray-500 text-sm">
                  —
                </div>
                <div className="p-4 flex items-center text-gray-500 text-sm truncate">
                  {folder.path}
                </div>
                <div className="p-4 flex items-center font-medium text-sm" style={{ color: APP_COLOR }}>
                  Ordner
                </div>
                <div className="p-4 flex items-center">
                  <button
                    type="button"
                    onClick={() => handleFolderSelect(folder.path)}
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 h-9 px-4 py-1"
                  >
                    Öffnen
                  </button>
                </div>
              </div>
            ))}
            {isDocumentsLoading && documents.length === 0 ? (
              <div className="p-8 text-center text-gray-500 col-span-5">
                <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
                Dokumente werden geladen...
              </div>
            ) : null}
            {documents.map((document) => (
              <div
                key={document.id}
                className="grid grid-cols-[3fr_2fr_2fr_1fr_1fr] border-b border-gray-200"
              >
                <div 
                  className="p-4 flex items-center gap-2 cursor-pointer transition-colors group"
                  onClick={() => handleDocumentClick(document.id, document.name)}
                  onMouseEnter={(e) => e.currentTarget.style.color = APP_COLOR}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                  title="Click to view/download"
                >
                  <div className="flex-shrink-0 p-1 rounded-md" style={{ backgroundColor: `${APP_COLOR}20` }}>
                    <FileText className="w-4 h-4" style={{ color: APP_COLOR }} />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate group-hover:underline">{document.name}</span>
                    {isSharePointEnabled && (
                    <span className="text-xs text-gray-500 truncate">
                      {document.sharepoint_full_path || "Kein SharePoint-Pfad"}
                    </span>
                    )}
                  </div>
                </div>
                <div className="p-4 flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage
                      src={document.profile_image_url || undefined}
                      alt="User avatar"
                    />
                    <AvatarFallback>
                      {document.email.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span>{document.email}</span>
                </div>
                <div className="p-4 flex items-center text-gray-600">
                  {new Date(document.created_at).toLocaleString("en-US", {
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </div>
                <div className="p-4 flex items-center">
                  {getStatusBadge(document.status)}
                </div>
                <div className="p-4 flex items-center">
                  <button
                    onClick={() => handleDelete(document.id)}
                    className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors"
                    disabled={
                      document.status === "processing" ||
                      document.status === "pending"
                    }
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination (Within Table) */}
            {totalPages > 1 ? (
              <div className="flex items-center justify-between p-4 bg-gray-50">
                {/* Left side: arrows + page numbers */}
                <div className="flex items-center gap-5">
                  {/* Previous button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage(previousPage);
                    }}
                    disabled={currentPage === 1}
                    className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent disabled:cursor-not-allowed"
                  >
                    &lt; Zurück
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-3">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <span
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`cursor-pointer ${
                            page === currentPage
                              ? "w-8 h-8 flex items-center justify-center rounded-full"
                              : "text-gray-700 hover:text-gray-900"
                          }`}
                          style={
                            page === currentPage
                              ? {
                                  color: APP_COLOR,
                                  backgroundColor: `${APP_COLOR}20`,
                                }
                              : undefined
                          }
                        >
                          {page}
                        </span>
                      ),
                    )}
                  </div>

                  {/* Next button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage < totalPages) setCurrentPage(nextPage);
                    }}
                    disabled={currentPage === totalPages}
                    className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent disabled:cursor-not-allowed"
                  >
                    Weiter &gt;
                  </button>
                </div>

                {/* Right side: "Showing x of y results" */}
                <div className="text-sm text-gray-500">
                  Zeige {documents.length} von {totalDocuments} Ergebnissen
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 text-center">
                <span className="text-sm text-gray-500">
                  Alle Dokumente auf einer Seite ({totalDocuments} Dokument
                  {totalDocuments !== 1 ? "e" : ""})
                </span>
              </div>
            )}
          </div>

          {/* Mobile Pagination */}
          <div className="lg:hidden mt-6 pb-4 mb-20">
            {totalPages > 1 ? (
              <div className="flex items-center justify-between bg-white border-2 border-red-500 rounded-lg p-4 shadow-lg">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage(previousPage);
                    }}
                    disabled={currentPage === 1}
                    className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors disabled:cursor-not-allowed"
                  >
                    &lt; Zurück
                  </button>
                  <span className="text-sm text-gray-500">
                    Seite {currentPage} von {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage < totalPages) setCurrentPage(nextPage);
                    }}
                    disabled={currentPage === totalPages}
                    className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors disabled:cursor-not-allowed"
                  >
                    Weiter &gt;
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  {documents.length} von {totalDocuments}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border-2 border-blue-500 rounded-lg p-4 text-center shadow-lg">
                <span className="text-sm text-gray-500">
                  Alle Dokumente auf einer Seite ({totalDocuments} Dokument
                  {totalDocuments !== 1 ? "e" : ""})
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-[500px] relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  {uploadFiles.length === 0
                    ? "Neues Dokument hochladen"
                    : uploadFiles.length === 1
                      ? "1 Dokument hochladen"
                      : `${uploadFiles.length} Dokumente hochladen`}
                </h2>
                <button
                  onClick={closeUploadModal}
                  disabled={isUploading}
                  className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={20} />
                </button>
              </div>
              {isSharePointEnabled && (
              <p className="text-xs text-gray-500 mb-4">
                Zielordner: <span className="font-medium">{selectedFolderDisplayName}</span>
              </p>
              )}
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="file">Datei(en) auswählen</Label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 lg:p-6 text-center"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                  >
                    {uploadFiles.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-sm font-medium">
                          {uploadFiles.length} Datei(en) ausgewählt:
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {uploadFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 p-2 rounded"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FileText size={14} className="flex-shrink-0" />
                                <span className="text-sm truncate">
                                  {file.name}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  ({(file.size / 1024 / 1024).toFixed(1)} MB)
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                                disabled={isUploading}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-2">
                          <button
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-transparent h-8 px-3"
                            onClick={clearAllFiles}
                            disabled={isUploading}
                          >
                            Alle entfernen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">
                          Datei(en) hier hineinziehen oder zum Durchsuchen
                          klicken
                        </p>
                        <Input
                          id="file"
                          type="file"
                          className="hidden"
                          multiple
                          onChange={(e) => handleFileSelect(e)}
                          accept=".pdf,.doc,.docx,.xlsx,.csv,.ifc"
                        />
                        <button
                          className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border bg-transparent h-9 px-3"
                          style={{ 
                            borderColor: APP_COLOR,
                            color: APP_COLOR
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${APP_COLOR}10`;
                            e.currentTarget.style.color = APP_COLOR;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = APP_COLOR;
                          }}
                          onClick={() =>
                            document.getElementById("file")?.click()
                          }
                        >
                          Dateien durchsuchen
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Unterstützte Formate: PDF, DOC, DOCX, CSV, XLSX, IFC. Max 25MB
                    pro Datei (IFC bis 500MB), 100MB insgesamt (500MB mit IFC). 
                    Die Verarbeitung kann mehrere Minuten dauern.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                <button
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 h-10 px-4 py-2 order-2 sm:order-1"
                  onClick={closeUploadModal}
                  disabled={isUploading}
                >
                  Abbrechen
                </button>
                <button
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2 order-1 sm:order-2"
                  style={{ 
                    backgroundColor: uploadFiles.length === 0 ? `${APP_COLOR}40` : APP_COLOR 
                  }}
                  onMouseEnter={(e) => {
                    if (uploadFiles.length > 0) {
                      e.currentTarget.style.backgroundColor = APP_COLOR;
                      e.currentTarget.style.filter = 'brightness(0.9)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (uploadFiles.length > 0) {
                      e.currentTarget.style.backgroundColor = APP_COLOR;
                      e.currentTarget.style.filter = 'none';
                    }
                  }}
                  onClick={handleUpload}
                  disabled={uploadFiles.length === 0 || isUploading}
                >
                  {isUploading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Wird hochgeladen... ({uploadFiles.length} Datei
                      {uploadFiles.length !== 1 ? "en" : ""})
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      {uploadFiles.length === 1
                        ? "1 Datei hochladen"
                        : `${uploadFiles.length} Dateien hochladen`}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
